import type { ReactNode } from 'react';
import { cn } from '@lib/utils';
import { useEnterSettle } from './liveMotion';

export interface LiveAvatarRingProps {
  /** When false the child renders untouched — an identity pass, zero wrapper cost. */
  active: boolean;
  /** Show the tiny LIVE tag under the ring (default true). */
  showTag?: boolean;
  /**
   * Border-radius utility for the ring, to stay concentric with the wrapped avatar
   * (make-interfaces-feel-better §1). Default `rounded-full` fits the circular carousel
   * avatars; the space hero avatar is a `rounded-2xl` squircle, so it passes a matching radius.
   */
  ringRadiusClassName?: string;
  children: ReactNode;
}

/**
 * Ring + tiny "LIVE" tag composed AROUND an existing (circular) avatar —
 * space avatars only in v1 (spec §4). Purely decorative: ring and tag are
 * `aria-hidden`; the accessible label ("live event in {space}") is the
 * consumer's job, since only it knows the space name.
 *
 * Appearance (§4.1): ring scales in 0.9→1 with opacity over 250ms
 * `ease-out-strong`; the tag staggers ~50ms behind. Under reduced motion the
 * scale is dropped and only the opacity fade remains.
 */
export function LiveAvatarRing({
  active,
  showTag = true,
  ringRadiusClassName = 'rounded-full',
  children,
}: LiveAvatarRingProps) {
  if (!active) {
    return children;
  }
  return (
    <ActiveAvatarRing showTag={showTag} ringRadiusClassName={ringRadiusClassName}>
      {children}
    </ActiveAvatarRing>
  );
}

/**
 * Module-level active branch so the enter-settle hook only mounts (and the
 * inactive path stays a hook-free identity render).
 */
function ActiveAvatarRing({
  showTag,
  ringRadiusClassName,
  children,
}: {
  showTag: boolean;
  ringRadiusClassName: string;
  children: ReactNode;
}) {
  const settled = useEnterSettle(() => false);
  const pose = settled ? 'scale-100 opacity-100' : 'scale-[0.9] opacity-0 motion-reduce:scale-100';

  return (
    <span className="relative inline-flex">
      {children}
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute -inset-1 border-2 border-mb-live',
          ringRadiusClassName,
          'transition-[opacity,scale] duration-[250ms] ease-out-strong',
          pose,
        )}
      />
      {showTag ? (
        // text-[10px] is deliberate (W1-3 M7b): no smaller token exists (text-xs
        // = 12px overwhelms a tag riding a 58px avatar) and 10px matches the
        // house micro-label precedent (e.g. the "Pending approval" pill).
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute -bottom-1.5 left-1/2 z-10 -translate-x-1/2 rounded-full bg-mb-live px-1.5 py-px text-[10px] leading-tight font-bold text-white',
            'transition-[opacity,scale] duration-[250ms] ease-out-strong delay-[50ms]',
            pose,
          )}
        >
          LIVE
        </span>
      ) : null}
    </span>
  );
}
